import * as Yup from 'yup';
import { isEqual, isValid } from 'date-fns';

import { Test, type TestProps } from './test.types';
import type { UserProps } from './user.types';
import { Request, type PaymentStatus, type Status } from './request.types';
import { calculateNextValidDate } from '../utils/assessmentDateTimeCalculator';

interface TestsSchemaOptions {
	isOnsiteLocation?: boolean;
}

const testsField = Yup.array(Test.schema)
	.default([] as TestProps[])
	.min(1)
	.required()
	.test('3-minimum-each-test', 'Each selected test must have a minimum quantity of 3', (tests) => {
		const selectedTests = Test.removeNilTest(tests.filter((test) => !Test.isFlatTravelFee(test.id)));

		if (selectedTests.length === 0) {
			return false;
		}

		return selectedTests.every((test) => Number(test.quantity) >= 3);
	});

/*
	Define attrs that need validations in yup. i.e add/edit a bulk request
	Otherwise, define them as ts types - see below
	
	Yup.InferType<typeof addSchema> & {
		amount?: number;
	};	
*/
const schema = Yup.object().shape({
	id: Yup.number().defined().optional(),
	// for users to choose a preferred date when creating a request
	prefAssessmentDates: Yup.array()
		.of(Yup.date().nullable().defined())
		.max(3)
		.required()
		.default(() => [calculateNextValidDate(new Date()), null])
		.test('no-duplicates', 'Preferred assessment dates must be unique (including time)', (dates) => {
			if (!Array.isArray(dates)) return false;

			const filtered = dates.filter((d): d is Date => d instanceof Date);

			const uniqueDates = filtered.reduce((prev: Date[], next) => {
				const alreadyExists = prev.some((d) => {
					return isEqual(d, next);
				});
				if (!alreadyExists) prev.push(next);
				return prev;
			}, []);

			// if false there is duplicated dates
			return uniqueDates.length === filtered.length;
		}),
	tests: testsField,
	location: Yup.string().default('').trim().required('Address is required'),
	tncAccepted: Yup.boolean()
		.oneOf([true], 'You must accept the terms and conditions.')
		.default(false)
		.required('You must accept the terms and conditions.'),
});

const addSchema = schema.pick(['tests', 'location', 'tncAccepted', 'prefAssessmentDates']);

export type AddBulkRequestProps = Yup.InferType<typeof addSchema> & {
	amount?: number;
};

const viewSchema = schema.pick(['id', 'tests', 'location', 'prefAssessmentDates']).shape({
	id: Yup.number().defined().required(),
	status: Yup.mixed<Status>().oneOf(Object.values(Request.status)).required(),
});

const editSchema = schema.pick(['id', 'tests', 'location', 'prefAssessmentDates']).shape({
	id: Yup.number().defined().required(),
	status: Yup.mixed<Status>().oneOf(Object.values(Request.status)).required(),
});

const preferredAssessmentDateLabels = [
	'Preferred Assessment Date',
	'Second Preferred Assessment Date',
	'Third Preferred Assessment Date',
];

export type BulkRequestProps = Yup.InferType<typeof viewSchema> & {
	user?: UserProps;
	createdAt?: Date;
	updatedAt?: Date;
	paymentStatus?: PaymentStatus;
	adminNote?: string;
	accountNote?: string;
	assessmentDates?: (Date | null)[];
	isArchived?: boolean;
	amount?: number;
};

const isAssessmentDatesSet = (dates: BulkRequestProps['assessmentDates'] = []) => {
	return dates.some((date) => {
		return isValid(new Date(date ?? ''));
	});
};

const isCancelled = (status: string | undefined) => {
	return status === 'CANCELLED';
};

const isPreferredAssessmentTimeEditingAllowed = (currentStatus: Status): boolean => {
	return Request.isPreferredAssessmentTimeEditingAllowed(currentStatus);
};

const preferredAssessmentDateSchema = schema.pick(['prefAssessmentDates']);
const locationSchema = schema.pick(['location']);
const reviewSchema = schema.pick(['tncAccepted']);
// a workaround to inject testing centre list into schema to help with audiometry constraint
const testsSchema = ({ isOnsiteLocation = false }: TestsSchemaOptions = {}) =>
	schema.pick(['tests']).shape({
		tests: testsField.test(
			'on-site-audiometry-minimum',
			"To ensure efficient use of our consultants' time, a minimum of 5 tests per type is required for on-site bookings.",
			(tests) => {
				if (!isOnsiteLocation) return true;

				const selectedTests = Test.removeNilTest(tests).filter((test) => !Test.isFlatTravelFee(test.id));
				const minimumFiveTestTypes = [
					Test.isAudiometry,
					Test.isInstantDrugAlcoholScreen,
					Test.isUnrineInstantDrugAlcoholTest,
				];
				const selectedTestTypeCount = minimumFiveTestTypes.filter((isTestType) =>
					selectedTests.some((test) => isTestType(test)),
				).length;

				if (selectedTestTypeCount !== 1 || selectedTests.length === 0) return true;

				const selectedTestCount = selectedTests.reduce((sum, test) => sum + Number(test.quantity || 0), 0);

				return selectedTestCount >= 5;
			},
		),
	});

export const BulkRequest = {
	addSchema,
	editSchema,
	testsSchema,
	preferredAssessmentDateSchema,
	locationSchema,
	reviewSchema,
	preferredAssessmentDateLabels,
	isAssessmentDatesSet,
	isCancelled,
	isPreferredAssessmentTimeEditingAllowed,
};
